import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary-gradient text-white dark:text-black shadow-sm hover:brightness-[0.97] active:brightness-[0.92] hover:shadow-md hover:-translate-y-0.5 disabled:brightness-100",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent text-foreground hover:bg-secondary hover:border-brand-accent/50",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-brand-accent underline-offset-4 hover:underline",
        "gradient-brand":
          "bg-primary-gradient text-primary-foreground rounded-sm overflow-hidden shadow-sm ring-4 ring-ring/30 transition-all duration-300 ease-out hover:brightness-[0.97] active:brightness-[0.92] hover:shadow-lg hover:shadow-primary/25 focus:outline-none disabled:opacity-70 disabled:brightness-100 disabled:cursor-not-allowed cursor-pointer",
        "gradient-brand-animated": "text-neutral-900 dark:text-neutral-100 font-semibold rounded-xl border-2 border-brand-accent bg-white dark:bg-neutral-950 hover:-translate-y-0.5 hover:shadow-md hover:shadow-brand-accent/15 active:translate-y-0 transition-all duration-300 cursor-pointer",
      },
      size: {
        default: "h-11 px-6 py-2",
        sm: "h-9 rounded-md px-4",
        lg: "h-14 rounded-xl px-8 text-base",
        xl: "h-16 rounded-xl px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
